import { OrganizationType } from '@/lib/enums/OrganizationType'
import { hasBroadPermission, type AppAbility } from './casl'

// The organization and role fields these checks read.
type UserAccessOrganization = { type?: string | null }
type UserAccessRole = { type?: string | null }
type UserManagementAction = 'list' | 'view' | 'create' | 'edit' | 'delete'

function rolePermissionSubject(role: UserAccessRole) {
  return role?.type === 'global' ? 'global-roles' : 'organization-roles'
}

export function isGlobalOrganization(
  organization: UserAccessOrganization | null | undefined
): boolean {
  return organization?.type === OrganizationType.Global.Value
}

function canManageUsersInOrganization(
  ability: AppAbility,
  action: UserManagementAction,
  organization: UserAccessOrganization | null | undefined
): boolean {
  if (ability.can('manage', 'all') || ability.can(action, 'all')) {
    return true
  }

  if (!organization) {
    return false
  }

  if (isGlobalOrganization(organization)) {
    const legacyReadAction = action === 'list' || action === 'view'

    return (
      hasBroadPermission(ability, action, 'global-users') ||
      (legacyReadAction && hasBroadPermission(ability, action, 'users'))
    )
  }

  return (
    hasBroadPermission(ability, action, 'organization-users') ||
    hasBroadPermission(ability, action, 'users')
  )
}

export function canAccessUsersInOrganization(
  ability: AppAbility,
  organization: UserAccessOrganization | null | undefined
): boolean {
  return (
    canManageUsersInOrganization(ability, 'list', organization) ||
    canManageUsersInOrganization(ability, 'view', organization)
  )
}

export function canCreateUsersInOrganization(
  ability: AppAbility,
  organization: UserAccessOrganization | null | undefined
): boolean {
  return canManageUsersInOrganization(ability, 'create', organization)
}

export function canEditUsersInOrganization(
  ability: AppAbility,
  organization: UserAccessOrganization | null | undefined
): boolean {
  return canManageUsersInOrganization(ability, 'edit', organization)
}

export function canDeleteUsersInOrganization(
  ability: AppAbility,
  organization: UserAccessOrganization | null | undefined
): boolean {
  return canManageUsersInOrganization(ability, 'delete', organization)
}

export function canAssignUserRoles(ability: Pick<AppAbility, 'can'>): boolean {
  return (
    ability.can('manage', 'all') ||
    ability.can('edit', 'roles') ||
    ability.can('assign', 'roles') ||
    ability.can('assign', 'organization-roles') ||
    ability.can('assign', 'global-roles')
  )
}

export function canViewRolePermissions(
  ability: Pick<AppAbility, 'can'>,
  role: UserAccessRole | null | undefined
): boolean {
  if (ability.can('manage', 'all') || ability.can('edit', 'roles') || canAssignUserRoles(ability)) {
    return true
  }

  if (!role) {
    return (
      ability.can('view', 'roles') ||
      ability.can('list', 'roles') ||
      ability.can('view', 'organization-roles') ||
      ability.can('list', 'organization-roles') ||
      ability.can('view', 'global-roles') ||
      ability.can('list', 'global-roles')
    )
  }

  const subject = rolePermissionSubject(role)

  return ability.can('view', subject) || ability.can('list', subject)
}
